// Toolbar button component with tooltip

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Toolbar button with icon and tooltip
 * Used in map editor and game UI toolbars
 */
class ToolbarButton extends BaseComponent {
  static properties = {
    active: { type: Boolean },
    disabled: { type: Boolean },
    badge: { type: Boolean },
    tooltip: { type: String },
    icon: { type: String },
    tooltipPosition: { type: String, attribute: 'tooltip-position' },
  };

  static styles = css`
    :host {
      display: inline-block;
    }

    .tool-button-wrapper {
      position: relative;
      z-index: 1;
    }

    .tool-button-wrapper:hover {
      z-index: 1002;
    }

    .tool-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #333;
      border: 1px solid #555;
      color: #fff;
      padding: 8px 12px;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 50px;
      font-size: 14px;
      font-weight: bold;
    }

    .tool-button:hover {
      background: #444;
      border-color: #666;
    }

    .tool-button:active {
      background: #222;
      transform: translateY(1px);
    }

    .tool-button.active {
      background: #0d6efd;
      border-color: #0d6efd;
    }

    .tool-button.active:hover {
      background: #0b5ed7;
      border-color: #0b5ed7;
    }

    .tool-button:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .tool-button:disabled:hover {
      background: #333;
      border-color: #555;
      transform: none;
    }

    .tool-tooltip {
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      white-space: nowrap;
      margin-left: 8px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s;
      z-index: 1001;
    }

    .tool-tooltip.tooltip-left {
      left: auto;
      right: 100%;
      margin-left: 0;
      margin-right: 8px;
    }

    .tool-tooltip.tooltip-top {
      left: 50%;
      top: auto;
      bottom: 100%;
      transform: translateX(-50%);
      margin-left: 0;
      margin-bottom: 8px;
    }

    .tool-button-wrapper:hover .tool-tooltip {
      opacity: 1;
    }

    .icon-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    ::slotted(svg) {
      width: 24px;
      height: 24px;
    }

    .overlay-icon {
      position: absolute;
      bottom: -4px;
      right: -6px;
      background: #333;
      border-radius: 50%;
      padding: 1px;
      line-height: 0;
    }

    .active .overlay-icon {
      background: #0d6efd;
    }

    ::slotted([slot='overlay']) {
      width: 10px;
      height: 10px;
    }

    .badge-dot {
      position: absolute;
      top: -8px;
      right: -8px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #ef4444;
      border: 2px solid #333;
      pointer-events: none;
    }

    .active .badge-dot {
      border-color: #0d6efd;
    }
  `;

  constructor() {
    super();
    this.active = false;
    this.disabled = false;
    this.badge = false;
    this.tooltip = '';
    this.icon = '';
    this.tooltipPosition = 'right';
  }

  render() {
    return html`
      <div class="tool-button-wrapper">
        <button class="tool-button ${this.active ? 'active' : ''}" ?disabled="${this.disabled}">
          <div class="icon-container">
            <slot></slot>
            ${this.icon ? html`<i class="bi bi-${this.icon}"></i>` : ''}
            <span class="overlay-icon"><slot name="overlay"></slot></span>
            ${this.badge ? html`<span class="badge-dot"></span>` : ''}
          </div>
        </button>
        <div class="tool-tooltip ${this.tooltipPosition === 'left' ? 'tooltip-left' : this.tooltipPosition === 'top' ? 'tooltip-top' : ''}">${this.tooltip}</div>
      </div>
    `;
  }
}

customElements.define('toolbar-button', ToolbarButton);
